import React from "react";

/**
 * What stands where the link card does while the pre-consultation preview is
 * up.
 *
 * There is no address to hand out here. A pre-consultation form opens for one
 * client, from a single-use link minted on their own profile, so this card
 * cannot offer a box and a Copy button — what it owes the practitioner instead
 * is where that link is found. Hence a card that reads as directions rather
 * than as a control: what the form collects, then the three steps that end
 * with it sent.
 */

const STEPS: Array<{ title: string; body: string }> = [
  {
    title: "Open My clients",
    body: "Find the client you're preparing a consultation for.",
  },
  {
    title: "Select the client",
    body: "Add them first if they're new.",
  },
  {
    title: "Send the link from the Details tab",
    body: "Share it by WhatsApp, SMS or email in one tap.",
  },
];

const PreConsultationCard: React.FC = () => (
  // The link card's own shell to the pixel: the two swap under one switch, and
  // a card that changed shape with the preview would read as the page moving
  // rather than as the same card answering about the other form.
  <div className="rounded-md border border-line bg-white p-5 sm:p-6 3xl:p-8">
    <h3 className="text-[22px] 3xl:text-[26px] font-semibold text-[#00424B]">
      Pre-consultation form
    </h3>
    <p className="mt-2 max-w-[620px] text-[14px] 3xl:text-[15px] leading-6 text-ink-muted">
      Collect each client's health history, lifestyle, food preferences and
      allergies before your first session. Every client gets their own private
      link.
    </p>

    {/* Named before the steps, for the same reason the benefits are: three
        numbered rows under a card about a form read as steps of filling it in
        until something says whose job they are. */}
    <h4 className="mt-6 text-[14px] 3xl:text-[15px] text-ink-muted 3xl:mt-8">
      How to send the form
    </h4>

    {/* An ordered list, and the numbers in it are drawn rather than counted by
        the browser: they carry the tinted disc the design gives them, and they
        stand at the size the benefit marks on the other card do so the two
        lists line up through the swap. */}
    <ol className="mt-4 space-y-4">
      {STEPS.map(({ title, body }, index) => (
        <li key={title} className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[10px] bg-brand-soft text-[14px] font-medium text-brand-dark"
          >
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="text-[14px] 3xl:text-[15px] font-medium text-ink">
              {title}
            </p>
            <p className="mt-0.5 text-[13px] 3xl:text-[14px] leading-5 text-ink-muted">
              {body}
            </p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

export default PreConsultationCard;
